import type React from "react";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { LetsTalkForm } from "@/components/LetsTalkForm";
import { fetchPostBySlug, fetchPublishedPosts, type UnifiedPost } from "@/lib/blog-db";
import { tagSlug } from "@/lib/blogTags";
import { absUrl, canonicalLink, canonicalUrl, routeMeta, SITE_NAME, SITE_URL } from "@/lib/seo";
import {
  extractBlockHeadings,
  extractMarkdownHeadings,
  pickRelatedPosts,
  slugifyHeading,
  type TocItem,
} from "@/lib/blog-related";
import { ArrowLeft, Calendar, List } from "lucide-react";

export const Route = createFileRoute("/blog/$slug")({
  loader: async ({ params }) => {
    const post = await fetchPostBySlug(params.slug);
    if (!post) throw notFound();
    const all = await fetchPublishedPosts();
    const related = pickRelatedPosts(post, all, 3);
    return { post, related };
  },
  head: (ctx) => {
    const { loaderData } = ctx;
    if (!loaderData) return { meta: [{ title: "Blog" }] };
    const { post } = loaderData as { post: UnifiedPost };
    const articleLd = {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: post.title,
      description: post.excerpt,
      image: post.coverImage ? absUrl(post.coverImage) : undefined,
      datePublished: post.date,
      dateModified: post.date,
      author: { "@type": "Organization", name: post.author },
      publisher: {
        "@type": "Organization",
        name: SITE_NAME,
        logo: { "@type": "ImageObject", url: `${SITE_URL}/favicon.ico` },
      },
      mainEntityOfPage: {
        "@type": "WebPage",
        "@id": canonicalUrl(`/blog/${post.slug}`),
      },
      keywords: post.tags?.join(", "),
    };
    return {
      meta: routeMeta(ctx, {
        title: `${post.title} — Texas Made Shade Blog`,
        description: post.excerpt,
        image: post.coverImage,
        type: "article",
        socialTitle: post.title,
        article: { publishedTime: post.date, author: post.author, tags: post.tags },
      }),
      links: [canonicalLink(`/blog/${post.slug}`)],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(articleLd),
        },
      ],
    };
  },
  notFoundComponent: () => (
    <div className="min-h-screen bg-cream">
      <SiteHeader />
      <main id="main-content" tabIndex={-1} className="mx-auto max-w-3xl px-4 py-24 text-center">
        <h1 className="font-display text-3xl font-bold text-navy-deep">
          Post not found
        </h1>
        <Link
          to="/blog"
          className="mt-6 inline-flex items-center gap-2 font-display text-sm font-bold uppercase tracking-wider text-rust"
        >
          <ArrowLeft className="h-4 w-4" /> Back to blog
        </Link>
      </main>
      <SiteFooter />
    </div>
  ),
  errorComponent: ({ error }) => (
    <div className="mx-auto max-w-3xl px-4 py-24 text-center">
      <h1 className="font-display text-2xl font-bold text-navy-deep">
        Something went wrong
      </h1>
      <p className="mt-2 text-navy-deep/70">{(error as Error)?.message}</p>
    </div>
  ),
  component: BlogPost,
});

function renderSeedBlock(block: NonNullable<UnifiedPost["bodyBlocks"]>[number], i: number) {
  switch (block.type) {
    case "h2":
      return (
        <h2
          key={i}
          id={slugifyHeading(block.text)}
          className="mt-10 scroll-mt-24 font-display text-2xl font-bold text-navy-deep md:text-3xl"
        >
          {block.text}
        </h2>
      );
    case "p":
      return <p key={i} className="mt-4 text-lg leading-relaxed text-navy-deep/85">{block.text}</p>;
    case "ul":
      return (
        <ul key={i} className="mt-4 list-disc space-y-2 pl-6 text-lg text-navy-deep/85">
          {block.items.map((it, j) => <li key={j}>{it}</li>)}
        </ul>
      );
    case "quote":
      return (
        <blockquote key={i} className="mt-6 border-l-4 border-rust bg-white p-5 italic text-navy-deep/90">
          "{block.text}"
          {block.who && (
            <footer className="mt-2 not-italic font-display text-xs uppercase tracking-[0.2em] text-navy-deep/60">
              — {block.who}
            </footer>
          )}
        </blockquote>
      );
    case "img":
      return <img key={i} src={absUrl(block.src)} alt={block.alt} className="my-8 w-full" loading="lazy" />;
  }
}

function headingText(children: React.ReactNode): string {
  if (typeof children === "string") return children;
  if (typeof children === "number") return String(children);
  if (Array.isArray(children)) return children.map(headingText).join("");
  if (children && typeof children === "object" && "props" in (children as object)) {
    return headingText((children as { props: { children: React.ReactNode } }).props.children);
  }
  return "";
}

function TableOfContents({ items }: { items: TocItem[] }) {
  if (items.length < 2) return null;
  return (
    <nav
      aria-label="In this article"
      className="mt-8 border-l-4 border-rust bg-white p-5 shadow-sm"
    >
      <h2 className="flex items-center gap-2 font-display text-xs font-bold uppercase tracking-[0.2em] text-navy-deep">
        <List className="h-3 w-3" aria-hidden="true" /> In this article
      </h2>
      <ol className="mt-3 space-y-2 text-sm text-navy-deep/85">
        {items.map((it, i) => (
          <li key={it.id} className="leading-snug">
            <span className="mr-2 font-display text-xs text-navy-deep/70">
              {String(i + 1).padStart(2, "0")}
            </span>
            <a href={`#${it.id}`} className="text-navy-deep underline-offset-4 hover:text-rust hover:underline">
              {it.text}
            </a>
          </li>
        ))}
      </ol>
    </nav>
  );
}

function BlogPost() {
  const { post, related } = Route.useLoaderData() as {
    post: UnifiedPost;
    related: UnifiedPost[];
  };

  const tocItems: TocItem[] =
    post.bodyMarkdown !== undefined
      ? extractMarkdownHeadings(post.bodyMarkdown)
      : extractBlockHeadings(post.bodyBlocks);

  return (
    <div className="min-h-screen bg-cream">
      <SiteHeader />
      <main id="main-content" tabIndex={-1}>
        <article>
          <div className="bg-navy-deep text-white">
            <div className="mx-auto max-w-3xl px-4 py-12 lg:px-8">
              <Link
                to="/blog"
                className="inline-flex items-center gap-2 font-display text-xs uppercase tracking-[0.2em] text-white/70 hover:text-white"
              >
                <ArrowLeft className="h-3 w-3" /> All posts
              </Link>
              <h1 className="mt-4 font-display text-4xl font-bold md:text-5xl">{post.title}</h1>
              <div className="mt-4 flex items-center gap-2 font-display text-xs uppercase tracking-[0.2em] text-white/70">
                <Calendar className="h-3 w-3" aria-hidden="true" />
                {new Date(post.date).toLocaleDateString("en-US", {
                  year: "numeric", month: "long", day: "numeric",
                })}
                <span aria-hidden="true">·</span>
                <span>{post.author}</span>
              </div>
              {post.tags && post.tags.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {post.tags.map((t) => (
                    <Link
                      key={t}
                      to="/blog/topic/$tag"
                      params={{ tag: tagSlug(t) }}
                      className="border border-white/30 px-2 py-0.5 text-[11px] uppercase tracking-wider text-white/80 hover:border-white hover:text-white"
                    >
                      {t}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>

          {post.coverImage && (
            <img
              src={absUrl(post.coverImage)}
              alt={post.title}
              loading="eager"
              decoding="async"
              fetchPriority="high"
              className="mx-auto mt-0 h-auto w-full max-w-5xl"
            />
          )}

          <div className="mx-auto max-w-3xl px-4 py-12 lg:px-8">
            <p className="text-xl text-navy-deep/85">{post.excerpt}</p>

            <TableOfContents items={tocItems} />

            {post.bodyMarkdown !== undefined ? (
              <div className="prose mt-6 max-w-none text-navy-deep/85
                              prose-headings:font-display prose-headings:text-navy-deep
                              prose-h2:mt-10 prose-h2:scroll-mt-24 prose-h2:text-2xl md:prose-h2:text-3xl
                              prose-a:text-rust hover:prose-a:text-navy-deep
                              prose-img:my-8 prose-blockquote:border-l-4
                              prose-blockquote:border-rust prose-blockquote:bg-white
                              prose-blockquote:p-5 prose-blockquote:italic">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    h2: ({ children, ...props }) => (
                      <h2 id={slugifyHeading(headingText(children))} {...props}>
                        {children}
                      </h2>
                    ),
                    img: ({ src, alt, title }) => {
                      const t = (title ?? "").toLowerCase();
                      const float = t.includes("right")
                        ? "float-none md:float-right md:ml-6 md:mb-4 md:w-1/2"
                        : t.includes("left")
                          ? "float-none md:float-left md:mr-6 md:mb-4 md:w-1/2"
                          : "w-full";
                      return (
                        <img
                          src={typeof src === "string" ? src : ""}
                          alt={alt ?? ""}
                          loading="lazy"
                          className={`my-6 h-auto rounded shadow-card ${float}`}
                        />
                      );
                    },
                  }}
                >
                  {post.bodyMarkdown}
                </ReactMarkdown>
              </div>
            ) : (
              post.bodyBlocks?.map((b, i) => renderSeedBlock(b, i))
            )}
          </div>
        </article>

        {related.length > 0 && (
          <section className="border-t border-navy-deep/10 bg-white py-14">
            <div className="mx-auto max-w-5xl px-4 lg:px-8">
              <h2 className="font-display text-2xl font-bold text-navy-deep">Related articles</h2>
              <p className="mt-1 text-sm text-navy-deep/60">
                Hand-picked based on shared topics.
              </p>
              <ul className="mt-6 grid gap-6 md:grid-cols-3">
                {related.map((p) => (
                  <li key={p.slug} className="flex flex-col border-2 border-navy-deep/10 bg-cream p-5">
                    {p.coverImage && (
                      <Link
                        to="/blog/$slug"
                        params={{ slug: p.slug }}
                        className="mb-4 block aspect-[16/10] overflow-hidden bg-navy-deep/5"
                      >
                        <img
                          src={absUrl(p.coverImage)}
                          alt={p.title}
                          loading="lazy"
                          className="h-full w-full object-cover transition-transform hover:scale-105"
                        />
                      </Link>
                    )}
                    <Link
                      to="/blog/$slug"
                      params={{ slug: p.slug }}
                      className="font-display text-lg font-bold leading-tight text-navy-deep hover:text-rust"
                    >
                      {p.title}
                    </Link>
                    <p className="mt-2 line-clamp-3 text-sm text-navy-deep/75">{p.excerpt}</p>
                    {p.tags && p.tags.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-1">
                        {p.tags.slice(0, 3).map((t) => (
                          <span
                            key={t}
                            className="border border-navy-deep/20 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-navy-deep/70"
                          >
                            {t}
                          </span>
                        ))}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </section>
        )}

        <LetsTalkForm />
      </main>
      <SiteFooter />
    </div>
  );
}
